import { getMuxHostedThumbnailUrl } from '@src/utility/story';
import { getFormattedDurationForStoryVideo } from '@src/utility/timeFormat';

import type { DashboardStory, OtherVideo } from '@src/types.ts/story';

/**
 * Shared helpers for rendering a story's clips (its `otherVideos`) in the
 * Story Manager table. Mirrors the derivation logic previously inlined in
 * `ClipDashboardContent` so the expandable-row view stays consistent with the
 * (now retired) Clips tab.
 */

/**
 * The clips of a story that are actually viewable: not hidden, and backed by a
 * primary video (directly or via an associated video).
 */
export const getVisibleClips = (story: DashboardStory): OtherVideo[] =>
  (story.otherVideos ?? []).filter(
    v =>
      !v.isHidden &&
      (v.videoFilePrimary || v.associatedVideos?.find(a => a.videoFilePrimary)),
  );

/**
 * Every clip associated with a story regardless of publish state, excluding
 * only user-hidden clips. The Story Manager row's clip count and the expanded
 * clip list both derive from this, so the count always matches what a user
 * sees after expanding the row (published clips included alongside drafts).
 */
export const getAllClips = (story: DashboardStory): OtherVideo[] =>
  (story.otherVideos ?? []).filter(v => !v.isHidden);

export const getClipAspectRatio = (clip: OtherVideo): string | undefined => {
  if (clip.videoFilePrimary) return clip.aspectRatio;
  return clip.associatedVideos?.find(v => v.videoFilePrimary)?.aspectRatio;
};

export const getClipPlaybackId = (clip: OtherVideo): string | undefined => {
  if (clip.videoFilePrimary) return clip.videoFilePrimary.video.muxPlaybackId;
  return clip.associatedVideos?.find(v => v.videoFilePrimary)?.videoFilePrimary
    ?.video.muxPlaybackId;
};

/** A frame to draw, paired with the shape of the video it was taken from. */
type ClipFrame = { url: string; aspectRatio?: string };

/**
 * The frame to show for a clip and the shape it was cut to, resolved together.
 *
 * The two have to come from the same link in the chain. A clip's thumbnail can
 * come from a published variant whose shape is not the clip's own — a rendered
 * 16:9 primary that stored no thumbnail falls through to the 9:16 variant — and
 * fitting one video's frame to another's shape crops a vertical frame to a
 * close-up of its middle, which is the bug this pairing exists to prevent.
 */
const resolveClipFrame = (clip: OtherVideo): ClipFrame | undefined => {
  if (clip.thumbnail?.url) {
    return { url: clip.thumbnail.url, aspectRatio: clip.aspectRatio };
  }
  if (clip.videoFilePrimary?.video?.thumbnailUrl) {
    return {
      url: clip.videoFilePrimary.video.thumbnailUrl,
      aspectRatio: clip.aspectRatio,
    };
  }
  const variant = clip.associatedVideos?.find(v => v.videoFilePrimary);
  const variantThumb = variant?.videoFilePrimary?.video?.thumbnailUrl;
  if (variantThumb) {
    return { url: variantThumb, aspectRatio: variant?.aspectRatio };
  }
  // Last resort: derive a real frame straight from Mux so every clip shows an
  // actual video frame rather than the placeholder gradient. `getClipPlaybackId`
  // and `getClipAspectRatio` pick the same video, so this link can't disagree.
  const muxPlaybackId = getClipPlaybackId(clip);
  if (muxPlaybackId) {
    return {
      url: getMuxHostedThumbnailUrl(muxPlaybackId),
      aspectRatio: getClipAspectRatio(clip),
    };
  }
  return undefined;
};

export const getClipThumbnailUrl = (clip: OtherVideo): string | undefined =>
  resolveClipFrame(clip)?.url;

export const getClipDurationLabel = (clip: OtherVideo): string =>
  getFormattedDurationForStoryVideo(clip).formattedDuration;

/**
 * How a frame of the given shape should sit inside the widescreen thumbnail
 * boxes the Story Manager uses. Widescreen frames fill the box as they always
 * have; anything taller (9:16, 1:1, 4:5) is centred whole against the box
 * background so a vertical clip shows its real framing rather than a zoomed-in
 * crop of its middle. An unrecorded shape keeps the original fill behaviour.
 */
export const getClipThumbnailFit = (
  aspectRatio?: string,
): 'cover' | 'contain' =>
  !aspectRatio || aspectRatio === '16:9' ? 'cover' : 'contain';

export type ClipThumbnailDisplay = {
  /** Undefined only when neither the clip nor its story has any frame. */
  url?: string;
  fit: 'cover' | 'contain';
};

/**
 * The frame to show for a clip, and how it should sit in the thumbnail box.
 *
 * A clip that has never been rendered — an unpublished draft or an untranslated
 * variant — has no video of its own, so nothing in its own chain resolves and
 * the row was left showing an empty placeholder. Falling back to the parent
 * story's poster keeps the row recognisable. That frame is the *story's* shape
 * rather than the clip's, so it fills the box instead of being centred to a
 * shape it doesn't have.
 */
export const getClipThumbnailDisplay = (
  clip: OtherVideo,
  storyThumbnailUrl?: string,
): ClipThumbnailDisplay => {
  const frame = resolveClipFrame(clip);
  if (frame) {
    return { url: frame.url, fit: getClipThumbnailFit(frame.aspectRatio) };
  }
  return { url: storyThumbnailUrl, fit: 'cover' };
};
